import type { PortalTokens } from './generated/types.gen';

import { env } from '@/env';

import { authEvents, type SignedOutReason } from './auth-events';
import { ApiError, unwrap, type Envelope } from './envelope';
import { createSessionChannel } from './session-channel';
import { tokenStore, type Tokens } from './token-store';

/**
 * Exchanges the refresh token for a new pair of tokens.
 *
 * The backend ROTATES the refresh token: each successful refresh invalidates the
 * one that was used. That makes concurrent refreshes actively harmful — if five
 * queries all get a 401 at the same moment and each starts its own refresh, the
 * second one presents a token the server has already retired and the whole
 * session is killed. The user is then signed out for no reason.
 *
 * So refreshes are funnelled through a single call:
 *   - within one tab, by sharing one in-flight promise;
 *   - across tabs, by taking a Web Lock (a browser feature that lets tabs on the
 *     same site take turns) and re-reading the store once the lock is held, so
 *     the loser of a race picks up the winner's new tokens instead of asking for
 *     another pair.
 */

const REFRESH_LOCK = 'activistarc-portal-auth-refresh';

/** Exported so a test can assert the request goes here, and so the list of
 *  paths that must never trigger a refresh can name it. */
export const REFRESH_ENDPOINT = '/activist-arc/organization/auth/refresh';

/**
 * The reply body as an envelope, or nothing when it is not even an object.
 * Every field of an envelope is optional, so any object reads as one; what its
 * data holds is checked by whoever uses it.
 */
function envelopeOf(body: unknown): Envelope<unknown> | undefined {
  return typeof body === 'object' && body !== null ? body : undefined;
}

/**
 * The reply's data once it is known to hold both tokens, or null. Nothing off
 * the wire is trusted by its type alone (review item 42): a reply missing a
 * token used to be stored as it came, and the next renewal presented
 * `undefined` as the refresh token.
 */
function tokenPairOf(data: unknown): PortalTokens | null {
  if (typeof data !== 'object' || data === null) return null;
  const { access_token, refresh_token } = data as Record<string, unknown>;
  if (typeof access_token !== 'string' || typeof refresh_token !== 'string') return null;
  return data as PortalTokens;
}

let inFlight: Promise<Tokens> | null = null;

async function requestNewTokens(): Promise<Tokens> {
  const current = tokenStore.get();
  if (!current) {
    throw new ApiError(401, null, 'There is no refresh token to use.');
  }

  // Deliberately a plain fetch rather than the generated client: the generated
  // client's fetch is the very thing that calls this, so going through it would
  // loop forever.
  //
  // The PORTAL endpoint. This used to post to /activist-arc/auth/access-token,
  // which is the mobile app's: it looks the identifier up in the app's user
  // table, and a portal identifier is in a different table, so it answered
  // "user not found" every time. Renewal could never succeed, and thirty
  // minutes after signing in the next click threw the person back to the
  // sign-in screen. The test that covered this counted requests without ever
  // checking where they went, so nothing caught it.
  const response = await fetch(`${env.apiBaseUrl}${REFRESH_ENDPOINT}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: current.refreshToken }),
  });

  const body = envelopeOf(await response.json().catch(() => undefined));

  if (!response.ok) {
    // The data too: a refused session says in it why it ended.
    throw new ApiError(
      response.status,
      body?.error ?? null,
      body?.message ?? null,
      undefined,
      body?.data,
    );
  }

  const data = tokenPairOf(unwrap(body, response.status));
  if (!data) {
    throw new ApiError(
      response.status,
      null,
      'The renewal reply did not carry a token pair.',
    );
  }
  /*
   * The session ended while this renewal was out - Log Out here, or a
   * sign-out from another tab. Its pair is thrown away: not stored, not
   * passed on, no person announced. Storing it would leave live tokens in a
   * signed-out tab; the server ends the session it named either way (D23).
   */
  if (!tokenStore.get()) {
    throw new ApiError(401, null, 'The session ended while it was being renewed.');
  }
  const next: Tokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  };
  // Store both before releasing the lock, so any waiting caller sees the pair.
  tokenStore.set(next);
  // And pass them to the other tabs before the lock is released, so none of
  // them presents the refresh token just retired (portal-session-rules, I2,
  // B9; review item 40).
  const profile = data.user ?? null;
  postToOtherTabs((channel) => channel.postTokens(next, profile));
  if (profile) authEvents.emitProfileUpdated(profile);
  return next;
}

/** Sends one message to the other tabs through a channel opened for it. */
function postToOtherTabs(
  send: (channel: ReturnType<typeof createSessionChannel>) => void,
) {
  const channel = createSessionChannel();
  send(channel);
  channel.close();
}

/**
 * The renewal, with one second chance (I3). Another tab may renew first and
 * pass its pair here while this tab's own request, holding the pair it just
 * retired, is being refused. When the store changed meanwhile, that new pair
 * is the answer; only a refusal with nothing new in the store fails.
 */
async function renewOrTakeNewer(): Promise<Tokens> {
  const started = tokenStore.get();
  try {
    return await requestNewTokens();
  } catch (error) {
    const now = tokenStore.get();
    if (
      error instanceof ApiError &&
      error.status === 401 &&
      now &&
      now.refreshToken !== started?.refreshToken
    ) {
      return now;
    }
    throw error;
  }
}

/**
 * @param usedAccessToken the token the failed request actually carried.
 *
 * It matters which token this is. Reading the store here instead would read it
 * at the moment of the call, not at the moment of the failure - so with two
 * requests both sent with T1, the first refreshes to T2, and the second then
 * reads T2 from the store, decides nothing has changed since "its" token, and
 * refreshes again. Every slow response cost a rotation for nothing, which is
 * exactly what the guard exists to prevent.
 */
export async function refreshTokens(usedAccessToken?: string): Promise<Tokens> {
  const stale = usedAccessToken ?? tokenStore.get()?.accessToken;

  const runGuarded = async (): Promise<Tokens> => {
    // Somebody else may have refreshed while this caller waited for the lock.
    const latest = tokenStore.get();
    if (latest && latest.accessToken !== stale) {
      return latest;
    }
    return renewOrTakeNewer();
  };

  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request(REFRESH_LOCK, runGuarded);
  }

  // No Web Locks (older browsers, and the test environment): fall back to
  // sharing one promise within this tab.
  inFlight ??= runGuarded().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Ends the session in this tab and tells the React layer, saying whether the
 * other tabs are to be told (portal-session-rules, design Decision 4). They
 * are not when this tab is ending because another one told it, which would
 * echo the message back and forth (I4). AuthProvider, which knows whose
 * session it was, writes the sign-out notice and tells them (I6, I7, B10).
 */
export function endSession(
  reason: SignedOutReason,
  { broadcast = true }: { broadcast?: boolean } = {},
): void {
  // Two requests refused at the same moment both arrive here. The first
  // ends the session; the second finds nothing to end, and must not tell
  // every listener a second time - a cache cleared again after the sign-in
  // page has started its own requests loses those.
  if (!tokenStore.get()) return;
  tokenStore.clear();
  authEvents.emitSignedOut(reason, { broadcast });
}
